import React from 'react';
import { WeeklyStats } from 'src/services/workouts.service';

const ZONE_COLORS: Record<string, string> = {
  '1': '#5c8ef0',
  '2': '#3ec9a7',
  '3': '#e8c34a',
  '4': '#e06a3c',
  '5': '#d4463e',
};

const ZONE_NUMBERS = ['1', '2', '3', '4', '5'];

const fmtHours = (minutes: number): string => {
  const whole = Math.round(minutes);
  if (whole < 60) return `${whole}m`;
  return `${Math.floor(whole / 60)}h ${String(whole % 60).padStart(2, '0')}m`;
};

const fmtSeconds = (seconds: number): string => fmtHours(seconds / 60);

/** "121-138 bpm" -> "121–138" for the compact bar labels */
const fmtRange = (range: string | undefined): string =>
  range ? range.replace(' bpm', '').replace(/(\d)-(\d)/, '$1–$2') : '';

const PHASE_LABELS: Record<string, string> = {
  build: 'Build phase',
  'general fitness': 'General fitness',
  peak: 'Peak phase',
  taper: 'Taper',
};

/** "Mon, Jul 27 – Sun, Aug 2" for the current Monday-based local week */
const weekRangeLabel = (): string => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return `${fmt(monday)} – ${fmt(sunday)}`;
};

const columnLabelStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: '1.1px',
  textTransform: 'uppercase',
  color: '#6b7688',
};

const subLabelStyle: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '1.1px',
  textTransform: 'uppercase',
  color: '#6b7688',
  marginTop: 3,
};

interface ZoneChartProps {
  title: string;
  titleColor: string;
  seconds: Record<string, number>;
  maxSeconds: number;
  ranges: Record<string, string>;
}

const ZoneChart: React.FC<ZoneChartProps> = ({ title, titleColor, seconds, maxSeconds, ranges }) => (
  <div>
    <div style={{ fontSize: 10.5, letterSpacing: '1px', textTransform: 'uppercase', color: titleColor, marginBottom: 8 }}>
      {title}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, alignItems: 'end', height: 96 }}>
      {ZONE_NUMBERS.map((n) => {
        const mins = Math.round((seconds[n] ?? 0) / 60);
        return (
          <div key={n} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 4, height: '100%' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: mins === 0 ? '#46527a' : '#dfe5f0' }}>{mins}m</div>
            <div
              style={{
                width: '100%',
                maxWidth: 42,
                borderRadius: '4px 4px 2px 2px',
                background: mins === 0 ? '#232c3e' : ZONE_COLORS[n],
                height: mins === 0 ? 3 : Math.max(Math.round(((seconds[n] ?? 0) / maxSeconds) * 76), 4),
              }}
            />
          </div>
        );
      })}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, borderTop: '1px solid #232c3e' }}>
      {ZONE_NUMBERS.map((n) => (
        <div key={n} style={{ textAlign: 'center', paddingTop: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#dfe5f0' }}>Z{n}</div>
          <div style={{ fontSize: 9.5, color: '#6b7688', marginTop: 1 }}>{fmtRange(ranges[n])}</div>
        </div>
      ))}
    </div>
  </div>
);

interface WeeklyStatsSectionProps {
  stats: WeeklyStats | null;
  loading: boolean;
}

export const WeeklyStatsSection: React.FC<WeeklyStatsSectionProps> = ({ stats, loading }) => {
  if (loading || !stats) {
    return (
      <section className="home__card" style={{ padding: '18px 20px', marginBottom: 20 }}>
        <div className="home__card-title">This Week</div>
        <div className="home__empty">{loading ? 'Crunching your week…' : 'Weekly stats unavailable.'}</div>
      </section>
    );
  }

  const { actuals, ideals } = stats;
  const ranges = stats.zone_hr_ranges ?? {};
  const trainingMinutes = actuals.training_minutes ?? actuals.duration_minutes;
  const targetMinutes = ideals.available && ideals.target_duration_hours != null
    ? ideals.target_duration_hours * 60
    : null;
  const remainingMinutes = targetMinutes != null ? Math.max(targetMinutes - trainingMinutes, 0) : null;
  const pct = targetMinutes ? Math.round((trainingMinutes / targetMinutes) * 100) : null;
  const excludedMinutes = actuals.duration_minutes - trainingMinutes;
  const coverageGap = actuals.duration_minutes - actuals.hr_covered_minutes > 0.5;

  const phaseLabel = ideals.available && ideals.phase
    ? (PHASE_LABELS[ideals.phase] ?? ideals.phase) +
      (ideals.weeks_until_race != null ? ` · ${ideals.weeks_until_race} weeks to race` : '')
    : null;

  const longTarget = ideals.available ? ideals.long_effort_target_seconds : null;
  const longZone = ideals.available && ideals.long_effort_zone
    ? ideals.long_effort_zone + (fmtRange(ranges['1']) ? ` · ${fmtRange(ranges['1'])} bpm` : '')
    : null;
  const longCurrent = actuals.long_effort
    ? `${actuals.long_effort.name ?? 'Long workout'} · ${fmtHours(actuals.long_effort.duration_minutes)}`
    : 'None yet';

  const targetSeconds = ideals.zone_target_seconds ?? {};
  const maxSeconds = Math.max(
    ...ZONE_NUMBERS.map((n) => actuals.zone_seconds[n] ?? 0),
    ...(ideals.available ? ZONE_NUMBERS.map((n) => targetSeconds[n] ?? 0) : [0]),
    1
  );

  return (
    <section style={{ background: '#1a2130', border: '1px solid #242e42', borderRadius: 10, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '16px 20px', borderBottom: '1px solid #232c3e' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: '#e8ecf4' }}>
            This Week
          </div>
          {phaseLabel && (
            <span
              style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '.5px', padding: '3px 10px',
                borderRadius: 99, color: '#8fa3ff', background: 'rgba(92,108,250,.12)',
                border: '1px solid rgba(92,108,250,.45)',
              }}
            >
              {phaseLabel}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#6b7688' }}>{weekRangeLabel()}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 2fr' }}>
        {/* Total duration (training time vs target) */}
        <div style={{ padding: '16px 20px 18px', borderRight: '1px solid #232c3e' }}>
          <div style={{ ...columnLabelStyle, marginBottom: 12 }}>Training time</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 33, fontWeight: 600, lineHeight: 1, color: '#e8ecf4' }}>
              {fmtHours(trainingMinutes)}
            </div>
            {targetMinutes != null && (
              <div style={{ fontSize: 13, color: '#9aa5b8' }}>of {fmtHours(targetMinutes)} target</div>
            )}
          </div>
          {targetMinutes != null ? (
            <>
              <div style={{ height: 7, borderRadius: 99, background: '#232c3e', marginTop: 14, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 99, background: '#5661f0', width: `${Math.min(pct ?? 0, 100)}%` }} />
              </div>
              <div style={{ fontSize: 12, color: remainingMinutes === 0 ? '#3ec9a7' : '#6b7688', marginTop: 8 }}>
                {pct}% · {remainingMinutes === 0 ? 'target met!' : `${fmtHours(remainingMinutes!)} remaining`}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12.5, lineHeight: 1.5, color: '#9aa5b8', marginTop: 12 }}>{ideals.reason}</div>
          )}
        </div>

        {/* Long effort */}
        <div style={{ padding: '16px 20px 18px', borderRight: '1px solid #232c3e', display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div style={{ ...columnLabelStyle, marginBottom: -1 }}>Long effort</div>
          <div>
            <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 22, fontWeight: 600, lineHeight: 1, color: longTarget != null ? '#e8ecf4' : '#6b7688' }}>
              {longTarget != null ? fmtSeconds(longTarget) : '—'}
            </div>
            <div style={subLabelStyle}>Target duration</div>
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: longZone ? '#dfe5f0' : '#6b7688' }}>{longZone ?? '—'}</div>
            <div style={subLabelStyle}>Recommended zone</div>
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: actuals.long_effort ? '#dfe5f0' : '#6b7688' }}>{longCurrent}</div>
            <div style={subLabelStyle}>This week's long effort</div>
          </div>
        </div>

        {/* Effort by heart rate zone */}
        <div style={{ padding: '16px 20px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
            <div style={columnLabelStyle}>Effort by heart rate zone</div>
            <div style={{ fontSize: 11, color: '#46527a' }}>minutes · bpm</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 18 }}>
            {ideals.available && (
              <ZoneChart title="Target" titleColor="#8fa3ff" seconds={targetSeconds} maxSeconds={maxSeconds} ranges={ranges} />
            )}
            <ZoneChart title="Current" titleColor="#6b7688" seconds={actuals.zone_seconds} maxSeconds={maxSeconds} ranges={ranges} />
          </div>
        </div>
      </div>

      {(excludedMinutes > 0.5 || coverageGap) && (
        <div style={{ padding: '10px 20px 14px', borderTop: '1px solid #232c3e' }}>
          {excludedMinutes > 0.5 && (
            <div style={{ fontSize: 11.5, color: '#6b7688' }}>
              {fmtHours(actuals.duration_minutes)} total moving time this week; {fmtHours(excludedMinutes)}{' '}
              was recovery-intensity (Zone 0) or unmeasured walking and doesn't count toward your training target.
            </div>
          )}
          {coverageGap && (
            <div style={{ fontSize: 11.5, color: '#6b7688', marginTop: excludedMinutes > 0.5 ? 4 : 0 }}>
              {fmtHours(actuals.hr_covered_minutes)} of {fmtHours(actuals.duration_minutes)} this week has
              heart-rate data — runs, hikes and rides without it count in full; walks need heart-rate data to count.
            </div>
          )}
        </div>
      )}
    </section>
  );
};
